// inv/src/pages/authentication/GeneralSettings.jsx
import { useState, useEffect } from "react";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import CurrentPlanInfo from "./CurrentPlanInfo";
import axios from "axios";
import { API_URL } from "../../config";
import { useAuth } from "../../components/authentication/AuthContext";
import { Pencil } from "lucide-react";

const GeneralSettings = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <p>Loading...</p>;
  }

  if (!user) {
    return <p>Please log in to view your profile.</p>;
  }

  const [displayName, setDisplayName] = useState(user.displayName);
  const [isSaving, setIsSaving] = useState(false);
  const [updateMessage, setUpdateMessage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    setUpdateMessage(null);

    try {
      const response = await axios.put(`${API_URL}/update-user-data`, {
        userId: user.user_id,
        displayName,
      });

      if (response.status === 200) {
        setUpdateMessage({ type: "success", text: "Display name updated successfully!" });
        setIsEditing(false);
      }
    } catch (error) {
      console.error("Error updating display name:", error);
      setUpdateMessage({ type: "error", text: "Failed to update display name. Please try again." });
    } finally {
      setIsSaving(false);
      setTimeout(() => setUpdateMessage(null), 3000);
    }
  };

  return (
    <div className="flex flex-col md:flex-row md:space-x-6 space-y-6 md:space-y-0">
      <div className="w-full md:w-1/2">
        <div className="p-6 bg-white text-gray-900 rounded-xl border border-stone-300 shadow-md">
          <h2 className="text-lg font-bold mb-4 flex items-center text-blue-gray-900">
            Your profile
          </h2>

          <form onSubmit={(e) => e.preventDefault()} className="space-y-5">
            <div className="grid grid-cols-[120px_1fr] items-center gap-3">
              <label htmlFor="email" className="block text-sm font-medium text-stone-700">
                Email
              </label>
              <div>
                {user.email}
              </div>
            </div>

            <div className="grid grid-cols-[120px_1fr] items-center gap-3">
              <label htmlFor="display_name" className="block text-sm font-medium text-stone-700">
                Name
              </label>
              <div className="flex items-center gap-2">
                <Input
                  id="display_name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  multi={false}
                  disabled={!isEditing}
                />
                <button
                  type="button"
                  onClick={() => setIsEditing(!isEditing)}
                  className="cursor-pointer p-1.5 rounded-full border border-stone-300 hover:bg-stone-50 transition-colors"
                  aria-label="Edit name"
                >
                  <Pencil className="w-4 h-4 text-stone-600" />
                </button>
              </div>
            </div>

            {updateMessage && (
              <div
                className={`rounded-lg border px-3 py-2 text-sm ${updateMessage.type === "success"
                  ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                  : "bg-rose-50 border-rose-200 text-rose-700"
                  }`}
              >
                {updateMessage.text}
              </div>
            )}

            <div className="pt-1">
              <Button
                onClick={handleSave}
                disabled={isSaving || !isEditing}
                className="min-w-36"
                type="submit"
                variant="secondary"
                size="sm"
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <div className="w-full md:w-1/2">
        <CurrentPlanInfo user={user} />
      </div>
    </div>
  );
};

export default GeneralSettings;